import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Activity, Info } from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ReferenceLine,
} from 'recharts';
import { useCurvaSFisica, type CurvaSFisicaPoint } from '@/hooks/useCurvaSFisica';
import { useEmpMonteCarloLatest, useEmpMonteCarloHistory, type MonteCarloResult } from '@/hooks/useEmpMonteCarlo';
import { useEmpRelatorioFinanciador, type RelatorioFinanciadorRow } from '@/hooks/useEmpRelatorioFinanciador';
import { useMcProjectionState } from '@/hooks/useMcProjectionState';
import { McProjectionBadge } from './McProjectionBadge';
import { McExecutiveKPIs } from './McExecutiveKPIs';
import { McConfidenceFooter } from './McConfidenceFooter';
import { McEventTimeline } from './McEventTimeline';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { parseDateOnly } from '@/lib/dateOnly';

/* ── Types ───────────────────────────────────────────────────────── */

interface Props {
  contratoId: string;
  dataFimPrevista?: string | null;
  /** Pass from parent to avoid duplicate hook call */
  mcLatest?: MonteCarloResult | null;
  /** Pass from parent to avoid duplicate hook call */
  relFin?: RelatorioFinanciadorRow | null;
}

/* ── helpers ─────────────────────────────────────────────────────── */

const fmtTick = (d: string) => {
  if (!d) return '';
  const parts = d.split('-');
  return parts.length >= 2 ? `${parts[1]}/${parts[0]?.slice(2)}` : d;
};

const fmtDateBR = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = parseDateOnly(iso.slice(0, 10));
  return d ? format(d, 'dd/MM/yyyy') : '—';
};

const dateDiffDays = (a: string, b: string): number =>
  Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);

const addDaysStr = (iso: string, days: number): string => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

/** Build deterministic projection line from last realized point using trend_spi */
function buildProjection(
  points: CurvaSFisicaPoint[],
  trendSpi: number | null,
): Array<{ data: string; projecao: number }> {
  if (!trendSpi || trendSpi <= 0) return [];
  const lastRealized = [...points].reverse().find(p => p.curva_realizada != null);
  if (!lastRealized || lastRealized.curva_realizada == null) return [];

  const startVal = lastRealized.curva_realizada;
  if (startVal >= 99) return [];

  const result: Array<{ data: string; projecao: number }> = [
    { data: lastRealized.data, projecao: startVal },
  ];

  const futurePoints = points.filter(
    p => p.data > lastRealized.data && p.curva_planejada != null,
  );
  if (futurePoints.length < 2) return result;

  let currentVal = startVal;
  let prevPlanned = lastRealized.curva_planejada ?? startVal;

  for (const fp of futurePoints) {
    if (fp.curva_planejada == null) continue;
    const plannedDelta = fp.curva_planejada - prevPlanned;
    const projectedDelta = plannedDelta * trendSpi;
    currentVal = Math.min(100, currentVal + projectedDelta);
    result.push({ data: fp.data, projecao: Math.round(currentVal * 10) / 10 });
    prevPlanned = fp.curva_planejada;
    if (currentVal >= 100) break;
  }

  return result;
}

/** Determine trend label from trend_spi */
function getTrendLabel(trendSpi: number | null, spiCurrent: number | null): string {
  if (trendSpi == null) return 'indeterminada';
  if (spiCurrent != null) {
    if (trendSpi > spiCurrent + 0.03) return 'melhorando';
    if (trendSpi < spiCurrent - 0.03) return 'piorando';
  }
  if (trendSpi >= 1.0) return 'estável';
  if (trendSpi >= 0.9) return 'estável';
  return 'piorando';
}

/** Build executive diagnosis from drivers_risco */
function buildDiagnosis(
  drivers: Array<{ fator: string; impacto: string; severidade: string }> | null,
  trendSpi: number | null,
  deviation: number,
): string {
  const parts: string[] = [];

  // Deviation-based
  if (deviation >= 2) {
    parts.push('Execução adiantada em relação ao cronograma');
  } else if (deviation >= -2) {
    parts.push('Progresso dentro da faixa esperada');
  } else if (deviation >= -5) {
    parts.push('Atraso moderado detectado');
  } else {
    parts.push('Atraso significativo');
  }

  // SPI trend
  if (trendSpi != null && trendSpi < 0.9) {
    parts.push('com tendência de agravamento');
  } else if (trendSpi != null && trendSpi >= 1.0) {
    parts.push('com ritmo de recuperação');
  }

  // Drivers
  if (drivers && drivers.length > 0) {
    const criticalDrivers = drivers
      .filter(d => d.severidade === 'alta' || d.severidade === 'critica')
      .map(d => d.fator.toLowerCase());
    if (criticalDrivers.length > 0) {
      parts.push(`causado principalmente por ${criticalDrivers.slice(0, 2).join(' e ')}`);
    }
  }

  return parts.join(', ') + '.';
}

/* ── Tooltip ─────────────────────────────────────────────────────── */

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  const isProjection = row?.p50 != null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs space-y-1 min-w-[180px]">
      <p className="font-semibold text-foreground border-b border-border/50 pb-1 mb-1">{fmtTick(label)}</p>
      {payload.filter((p: any) => p.value != null).map((p: any) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{p.name}</span>
          </div>
          <span className="font-medium tabular-nums">{Number(p.value).toFixed(1)}%</span>
        </div>
      ))}
      {isProjection && row?.p10 != null && row?.p80 != null && (
        <div className="pt-1 mt-1 border-t border-border/50 text-[10px] text-muted-foreground">
          Faixa: {Number(row.p10).toFixed(1)}% – {Number(row.p80).toFixed(1)}%
        </div>
      )}
    </div>
  );
};

/* ── Component ───────────────────────────────────────────────────── */

export const PanoramaProgressoChart: React.FC<Props> = ({
  contratoId,
  dataFimPrevista,
  mcLatest: mcLatestProp,
  relFin: relFinProp,
}) => {
  const { points, isLoading } = useCurvaSFisica(contratoId);
  const mcState = useMcProjectionState(contratoId);

  // Use props if provided, otherwise call hooks (React Query deduplicates anyway)
  const { data: mcLatestHook } = useEmpMonteCarloLatest(mcLatestProp !== undefined ? null : contratoId);
  const { data: relFinHook } = useEmpRelatorioFinanciador(relFinProp !== undefined ? null : contratoId);
  const { data: mcHistory = [] } = useEmpMonteCarloHistory(contratoId);

  const mcLatest = mcLatestProp !== undefined ? mcLatestProp : mcLatestHook;
  const relFin = relFinProp !== undefined ? relFinProp : relFinHook;

  const trendSpiRaw = relFin?.trend_spi;
  const trendSpi = trendSpiRaw != null && trendSpiRaw !== '' ? Number(trendSpiRaw) : null;
  // Fallback: derive SPI from MC input_snapshot when relFin view is unavailable
  const mcSpi = mcLatest?.input_snapshot
    ? Number((mcLatest.input_snapshot as any).spi_decayed ?? (mcLatest.input_snapshot as any).spi_mean ?? null)
    : null;
  const validTrendSpi = (trendSpi != null && !isNaN(trendSpi)) ? trendSpi
    : (mcSpi != null && !isNaN(mcSpi) && mcSpi > 0) ? mcSpi
    : null;
  const spiCurrent = relFin?.spi != null ? Number(relFin.spi) : validTrendSpi;

  // Merge all layers into a single dataset
  const { chartData, deviation, prognosis, showP90Line } = useMemo(() => {
    if (!points || points.length < 2)
      return { chartData: [], deviation: null, prognosis: null };

    const projection = buildProjection(points, validTrendSpi);
    const projMap = new Map(projection.map(p => [p.data, p.projecao]));

    // Build merged data — include MC probabilistic envelope
    const merged = points.map(p => ({
      data: p.data,
      planejado: p.curva_planejada,
      realizado: p.curva_realizada,
      projecao: projMap.get(p.data) ?? null,
      p10: p.p10 ?? null,
      p50: p.p50 ?? null,
      p80: p.p80 ?? null,
    }));

    // Add projection points beyond planned range
    for (const pp of projection) {
      if (!merged.find(m => m.data === pp.data)) {
        merged.push({ data: pp.data, planejado: null, realizado: null, projecao: pp.projecao, p10: null, p50: null, p80: null });
      }
    }

    // B3: Insert synthetic points for MC dates so ReferenceLine renders
    // R3+R6: Compute chart domain limit — max 3x contract duration from start, or P80+20%
    const contractDuration = dataFimPrevista
      ? dateDiffDays(points[0]?.data || '', dataFimPrevista.slice(0, 10))
      : 365;
    const domainMaxDate = (() => {
      const candidates: string[] = [];
      // P80 date + 20% buffer
      if (mcLatest?.p80_data) {
        const p80 = mcLatest.p80_data.slice(0, 10);
        const buffer = Math.ceil(contractDuration * 0.2);
        candidates.push(addDaysStr(p80, buffer));
      }
      // 3x contract duration from start
      if (points[0]?.data && contractDuration > 0) {
        candidates.push(addDaysStr(points[0].data, contractDuration * 3));
      }
      // P50 date + 6 months (always show P50 comfortably)
      if (mcLatest?.p50_data) {
        candidates.push(addDaysStr(mcLatest.p50_data.slice(0, 10), 180));
      }
      // Take the minimum of candidates to keep chart compact
      if (candidates.length > 0) {
        candidates.sort();
        return candidates[Math.floor(candidates.length / 2)]; // median
      }
      return null;
    })();

    // R6: Only show P90 line if within reasonable range (< 3x contract duration)
    const showP90Line = mcLatest?.p90_data && dataFimPrevista
      ? dateDiffDays(dataFimPrevista.slice(0, 10), mcLatest.p90_data.slice(0, 10)) < contractDuration * 2
      : !!mcLatest?.p90_data;

    const mcDates = [
      mcLatest?.p50_data?.slice(0, 10),
      showP90Line ? mcLatest?.p90_data?.slice(0, 10) : null,
    ].filter(Boolean) as string[];

    for (const md of mcDates) {
      if (!merged.find(m => m.data === md)) {
        merged.push({ data: md, planejado: null, realizado: null, projecao: null, p10: null, p50: null, p80: null });
      }
    }

    // Also ensure contract end date is in range
    if (dataFimPrevista) {
      const endDate = dataFimPrevista.slice(0, 10);
      if (!merged.find(m => m.data === endDate)) {
        merged.push({ data: endDate, planejado: null, realizado: null, projecao: null, p10: null, p50: null, p80: null });
      }
    }

    // R3: Trim data to domain limit to prevent chart compression
    if (domainMaxDate) {
      const cutoff = domainMaxDate;
      const trimmed = merged.filter(m => m.data <= cutoff);
      // Always keep last point for visual completeness
      if (trimmed.length < merged.length && trimmed.length > 0) {
        merged.length = 0;
        merged.push(...trimmed);
      }
    }

    merged.sort((a, b) => a.data.localeCompare(b.data));

    // Sample if too many points
    let sampled = merged;
    if (merged.length > 120) {
      const step = Math.ceil(merged.length / 120);
      sampled = merged.filter((_, i) => i % step === 0);
      if (sampled[sampled.length - 1] !== merged[merged.length - 1]) {
        sampled.push(merged[merged.length - 1]);
      }
      // Re-ensure MC synthetic points survive sampling
      for (const md of mcDates) {
        if (!sampled.find(s => s.data === md)) {
          const orig = merged.find(m => m.data === md);
          if (orig) sampled.push(orig);
        }
      }
      sampled.sort((a, b) => a.data.localeCompare(b.data));
    }

    // Deviation: use last point with curva_realizada != null (last-known-value)
    const today = new Date().toISOString().slice(0, 10);
    let dev: {
      planned_at_last: number; realized: number; diff: number;
      planned_today: number | null; atraso_real: number | null;
    } | null = null;

    for (const p of points) {
      if (p.data <= today && p.curva_planejada != null && p.curva_realizada != null) {
        dev = {
          planned_at_last: p.curva_planejada,
          realized: p.curva_realizada,
          diff: p.curva_realizada - p.curva_planejada,
          planned_today: null,
          atraso_real: null,
        };
      }
    }

    // Compute real delay: Planned TODAY vs Last Realized
    if (dev) {
      const todayPoint = [...points].reverse().find(p => p.data <= today && p.curva_planejada != null);
      dev.planned_today = todayPoint?.curva_planejada ?? null;
      dev.atraso_real = dev.planned_today != null ? dev.realized - dev.planned_today : null;
    }

    // Build executive prognosis (B1) — now includes probability + risk drivers
    let prog: string | null = null;
    if (dev) {
      const lines: string[] = [];

      // Line 1: Progress — last realized vs planned at that date
      lines.push(`Progresso atual: ${dev.realized.toFixed(1)}% vs ${dev.planned_at_last.toFixed(1)}% planejado na última medição (desvio de ${dev.diff >= 0 ? '+' : ''}${dev.diff.toFixed(1)} p.p.).`);

      // Line 1b: Real delay vs today's baseline
      if (dev.atraso_real != null && dev.planned_today != null) {
        lines.push(`Atraso real (planejado hoje ${dev.planned_today.toFixed(1)}% vs realizado ${dev.realized.toFixed(1)}%): ${dev.atraso_real >= 0 ? '+' : ''}${dev.atraso_real.toFixed(1)} p.p.`);
      }

      // Line 2: prob_on_time + percentile dates (merged from McProbCard)
      if (mcLatest?.prob_on_time != null) {
        lines.push(`Probabilidade de cumprimento do prazo: ${mcLatest.prob_on_time.toFixed(0)}%.`);
      }

      // Line 3: P50 date
      if (mcLatest?.p50_data) {
        lines.push(`Previsão mais provável (mediana): ${fmtDateBR(mcLatest.p50_data)}.`);
      }

      // Line 4: P90 date
      if (mcLatest?.p90_data) {
        lines.push(`Cenário conservador: ${fmtDateBR(mcLatest.p90_data)}.`);
      }

      // Line 5: Trend
      const trend = getTrendLabel(validTrendSpi, spiCurrent);
      lines.push(`Tendência: ${trend}.`);

      // Line 6: Diagnosis — use atraso_real for severity assessment
      const diagnosis = buildDiagnosis(
        mcLatest?.drivers_risco ?? null,
        validTrendSpi,
        dev.atraso_real ?? dev.diff,
      );
      lines.push(`Diagnóstico: ${diagnosis}`);

      // Line 7+: Risk drivers (merged from Fatores de Risco card)
      const drivers = Array.isArray(mcLatest?.drivers_risco) ? mcLatest!.drivers_risco : [];
      const criticalDrivers = drivers.filter((d: any) => d.severidade === 'alto' || d.severidade === 'critico' || d.severidade === 'alta' || d.severidade === 'critica' || d.severidade === 'medio');
      if (criticalDrivers.length > 0) {
        const driverStrs = criticalDrivers.slice(0, 3).map((d: any) => `• ${d.fator}: ${d.impacto}`);
        lines.push(`Fatores de risco:\n${driverStrs.join('\n')}`);
      }

      prog = lines.join('\n');
    }

    return { chartData: sampled, deviation: dev, prognosis: prog, showP90Line };
  }, [points, validTrendSpi, mcLatest, spiCurrent, dataFimPrevista]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
          <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
          <span>Carregando Panorama de Progresso...</span>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length < 2) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 text-muted-foreground text-sm">
          Dados insuficientes para gerar o Panorama de Progresso. Importe um cronograma com atividades e pesos físicos.
        </CardContent>
      </Card>
    );
  }

  // Badge uses atraso_real (planned today vs last realized) for severity
  const badgeValue = deviation?.atraso_real ?? deviation?.diff ?? null;
  const deviationBadge = deviation && badgeValue != null
    ? badgeValue >= 0
      ? { label: 'No prazo', variant: 'outline' as const }
      : badgeValue > -5
        ? { label: `${badgeValue.toFixed(1)} p.p.`, variant: 'secondary' as const }
        : { label: `${badgeValue.toFixed(1)} p.p.`, variant: 'destructive' as const }
    : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-primary" />
           <CardTitle className="text-base">Panorama de Progresso</CardTitle>
            <McProjectionBadge state={mcState} />
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs max-w-[320px]">
                <p>Visão unificada do contrato: Baseline (planejado), Execução Real, Projeção de tendência e previsões probabilísticas de término.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          {mcLatest?.p50_data && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-xs cursor-help">
                    Prev.: {fmtDateBR(mcLatest.p50_data)}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  <p>P50 (mediana) — data mais provável de conclusão via simulação Monte Carlo</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {deviationBadge && (
            <Badge variant={deviationBadge.variant}>
              {deviationBadge.label}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Executive KPI Cards */}
        <McExecutiveKPIs
          realized={deviation?.realized ?? null}
          planned={deviation?.planned_at_last ?? null}
          spi={validTrendSpi}
          mcLatest={mcLatest ?? null}
          dataFimPrevista={dataFimPrevista}
        />

        <div className="h-[360px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <defs>
                <linearGradient id="panoramaRealizadoGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(162, 46%, 33%)" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="hsl(162, 46%, 33%)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="mcEnvelopeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(270, 40%, 52%)" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="hsl(270, 40%, 52%)" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="data"
                tickFormatter={fmtTick}
                className="text-xs fill-muted-foreground"
                tick={{ fontSize: 10 }}
              />
              <YAxis
                domain={[0, 105]}
                tickFormatter={(v) => `${v}%`}
                className="text-xs fill-muted-foreground"
                tick={{ fontSize: 10 }}
                width={50}
                label={{
                  value: 'Progresso (%)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -5,
                  style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' },
                }}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px' }} iconType="line" />

              <ReferenceLine
                y={100}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="6 3"
                label={{ value: '100%', position: 'right', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              />

              {/* MC P50 — "Previsão mais provável" */}
              {mcLatest?.p50_data && (
                <ReferenceLine
                  x={mcLatest.p50_data.slice(0, 10)}
                  stroke="hsl(var(--chart-mc-median))"
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  label={{ value: 'Prev. provável', position: 'top', fontSize: 9, fill: 'hsl(270, 40%, 52%)' }}
                />
              )}

              {showP90Line && mcLatest?.p90_data && (
                <ReferenceLine
                  x={mcLatest.p90_data.slice(0, 10)}
                  stroke="#dc2626"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  label={{ value: 'Conservador', position: 'top', fontSize: 9, fill: '#dc2626' }}
                />
              )}

              {/* Contract end date */}
              {dataFimPrevista && (
                <ReferenceLine
                  x={dataFimPrevista.slice(0, 10)}
                  stroke="#6b7280"
                  strokeDasharray="6 3"
                  strokeWidth={1}
                  label={{ value: 'Prazo', position: 'top', fontSize: 10, fill: '#6b7280' }}
                />
              )}

              {/* Layer 0: Monte Carlo P10-P80 Risk Envelope */}
              {chartData.some(d => d.p50 != null) && (
                <>
                  <Area
                    type="monotone"
                    dataKey="p80"
                    name="Cone de Incerteza (P10–P80)"
                    stroke="hsl(var(--chart-mc-envelope))"
                    strokeWidth={0.5}
                    strokeDasharray="3 3"
                    fill="url(#mcEnvelopeGrad)"
                    fillOpacity={1}
                    dot={false}
                    connectNulls={false}
                    legendType="none"
                  />
                  <Area
                    type="monotone"
                    dataKey="p10"
                    name="P10 (Otimista)"
                    stroke="none"
                    fill="hsl(var(--background))"
                    fillOpacity={1}
                    dot={false}
                    connectNulls={false}
                    legendType="none"
                  />
                </>
              )}

              {/* Layer 1: Baseline (PV) — Gray dashed */}
              <Area
                type="monotone"
                dataKey="planejado"
                name="Planejado (Baseline)"
                stroke="#9ca3af"
                strokeWidth={2}
                strokeDasharray="8 4"
                fill="#9ca3af"
                fillOpacity={0.03}
                dot={false}
                connectNulls
              />

              {/* Layer 2: Executed (EV) — Green solid */}
              <Area
                type="monotone"
                dataKey="realizado"
                name="Realizado"
                stroke="#16a34a"
                strokeWidth={2.5}
                fill="url(#panoramaRealizadoGrad)"
                dot={false}
                connectNulls
              />

              {/* Layer 3: MC P50 Projection (median) */}
              {chartData.some(d => d.p50 != null) && (
                <Area
                  type="monotone"
                  dataKey="p50"
                  name="Projeção MC (P50)"
                  stroke="hsl(var(--chart-mc-median))"
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  fill="none"
                  dot={false}
                  connectNulls={false}
                />
              )}

              {/* Layer 4: Deterministic Projection (SPI-based) — Orange dashed */}
              <Area
                type="monotone"
                dataKey="projecao"
                name="Projeção de tendência"
                stroke="#ea580c"
                strokeWidth={2}
                strokeDasharray="4 2"
                fill="#ea580c"
                fillOpacity={0.04}
                dot={false}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Executive prognosis footer */}
        <div className="mt-3 space-y-2">
          {deviation && (
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span>Realizado: <strong className="text-foreground">{deviation.realized.toFixed(1)}%</strong></span>
              <span>Planejado (medição): <strong className="text-foreground">{deviation.planned_at_last.toFixed(1)}%</strong></span>
              <span>Desvio: <strong className={cn(deviation.diff >= 0 ? 'text-success' : 'text-destructive')}>
                {deviation.diff >= 0 ? '+' : ''}{deviation.diff.toFixed(1)} p.p.
              </strong></span>
              {deviation.atraso_real != null && deviation.planned_today != null && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">Atraso real: <strong className={cn(
                        deviation.atraso_real >= 0 ? 'text-success' : 'text-destructive'
                      )}>{deviation.atraso_real >= 0 ? '+' : ''}{deviation.atraso_real.toFixed(1)} p.p.</strong></span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <p>Planejado hoje ({deviation.planned_today.toFixed(1)}%) vs último realizado ({deviation.realized.toFixed(1)}%)</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {validTrendSpi != null && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">SPI tendência: <strong className={cn(
                        validTrendSpi >= 1 ? 'text-success' : validTrendSpi >= 0.9 ? 'text-warning' : 'text-destructive'
                      )}>{validTrendSpi.toFixed(2)}</strong></span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      <p>Schedule Performance Index — tendência. ≥1.0 = no prazo, &lt;0.9 = risco elevado.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          {prognosis && (
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2.5 border border-border/50 space-y-0.5">
              <p className="font-semibold text-foreground text-xs mb-1">Prognóstico Executivo</p>
              {prognosis.split('\n').map((line, i) => (
                <p key={i} className={cn(
                  i === prognosis.split('\n').length - 1 ? 'text-foreground/80 italic' : '',
                )}>
                  {line}
                </p>
              ))}
            </div>
          )}

          {/* Confidence Footer */}
          <McConfidenceFooter mcLatest={mcLatest ?? null} mcState={mcState} />

          {/* Event Timeline */}
          {mcHistory.length >= 2 && (
            <McEventTimeline history={mcHistory} maxItems={4} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};
